import { z } from 'zod';

/** Allowed MIME types for adjuntos (certificates, supporting docs, photos). */
export const ADJUNTO_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
] as const;

export type AdjuntoMimeType = (typeof ADJUNTO_MIME_TYPES)[number];

/** Allowed adjunto types (categories). */
export const ADJUNTO_TIPOS = ['certificado', 'respaldo', 'foto_adjunta'] as const;
export type AdjuntoTipo = (typeof ADJUNTO_TIPOS)[number];

/** Max file size for adjuntos: 10 MB. */
export const ADJUNTO_MAX_FILE_SIZE = 10 * 1024 * 1024;

/** Max adjuntos per ticket. */
export const ADJUNTO_MAX_PER_TICKET = 30;

/** Request body for generating a presigned upload URL for an adjunto. */
export const AdjuntoPresignSchema = z.object({
  fileName: z.string().min(1).max(255),
  mimeType: z.enum(ADJUNTO_MIME_TYPES),
  fileSize: z.number().int().positive().max(ADJUNTO_MAX_FILE_SIZE),
  tipo: z.enum(ADJUNTO_TIPOS),
});

/** Request body to confirm an adjunto upload. */
export const AdjuntoConfirmSchema = z.object({
  s3Key: z.string().min(1),
  fileName: z.string().min(1).max(255),
  mimeType: z.enum(ADJUNTO_MIME_TYPES),
  fileSize: z.number().int().positive(),
  tipo: z.enum(ADJUNTO_TIPOS),
});

export type AdjuntoPresign = z.infer<typeof AdjuntoPresignSchema>;
export type AdjuntoConfirm = z.infer<typeof AdjuntoConfirmSchema>;

/** Response type for a single adjunto with presigned view URL. */
export interface AdjuntoWithUrl {
  id: number;
  tipo: string;
  nombre: string;
  s3Key: string;
  mimeType: string;
  size: number;
  createdAt: string;
  /** Presigned GET URL for inline viewing (PDF/image render in tab/iframe). */
  url: string;
  /** Presigned GET URL with Content-Disposition: attachment — forces a real
   *  download even cross-origin (the `<a download>` attr is ignored for
   *  cross-origin S3 URLs). Mirrors the ticket-PDF `?download=1` pattern. */
  downloadUrl: string;
}

/** Progress breakdown per section for close-validation. */
export interface CloseProgress {
  checklist: { total: number; completed: number; percentage: number };
  cuadrilla: { count: number; hasLider: boolean };
  tooltime: { count: number; totalHH: number };
  fotos: { count: number };
  observaciones: { has: boolean; requiredForNok: boolean };
}

/** Validation errors returned before closing a ticket. */
export interface CloseValidationResult {
  valid: boolean;
  errors: CloseValidationError[];
  progress: CloseProgress;
}

export interface CloseValidationError {
  code: string;
  message: string;
  count?: number;
  section?: string;
}
